import { FC } from 'react';
import { useTranslation } from '@packages/utils';
import EditableInput from 'components/Base/EditableFields/EditableInput';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useEditTitleMutation } from 'redux/apiSlice/events.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import useGetValidationData from 'components/Base/Form/validationRules';
import { Flex } from 'antd';
import { EventsOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { TTitleProps } from './Title.types';
import { STitleInputWrapper } from './Title.styles';

const Title: FC<TTitleProps> = ({ id, title, readonly }) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.basic-info.title',
  });
  const { validationRules } = useGetValidationData();
  const { showToastNotification } = useShowToastNotification();
  const [editTitle, { isLoading, isError }] = useEditTitleMutation();

  const onEdit = async (value: string) => {
    try {
      await editTitle({
        id,
        value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-title" gap={8}>
      <Flex align="center" gap={8} style={{ width: 160, flexShrink: 0 }}>
        <EventsOutline size={24} />
        <Text color="#808080">{t('LABEL')}</Text>
      </Flex>
      <STitleInputWrapper>
        <EditableInput
          onEdit={onEdit}
          title={title}
          isError={isError}
          isLoading={isLoading}
          readonly={readonly}
          canRemove={false}
          emptyText={t('EMPTY_TEXT')}
          rules={[
            validationRules.requiredField(),
            // mirrors the events.title column limit; the BE rejects >255 with a 400
            { max: 255, message: t('MAX_LENGTH_MESSAGE') },
          ]}
          validateTrigger="onSubmit"
          backIcon
          fitContent
          wrapperClassName="w-100"
        />
      </STitleInputWrapper>
    </Flex>
  );
};

export default Title;
